import React, { useEffect, useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";
import { SPEAKERS } from "./constants";
import { CaretLeft, CaretRight } from "phosphor-react";
import SpeakerCardFrontView from "./FeaturedSpeakerComponents/SpeakerCardFrontView";
import SpeakerCardBackView from "./FeaturedSpeakerComponents/SpeakerCardBackView";
import SpeakerBioModal from "./FeaturedSpeakerComponents/SpeakerBioModal";
import ResponsiveSpeakerCarousel from "./FeaturedSpeakerComponents/ResponsiveCarousel";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const FeaturedSpeakers = () => {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sample speaker data - replace with actual data and images

  // Add refs to array
  const addToRefs = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  const openModal = (speaker) => {
    setSelectedSpeaker(speaker);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSpeaker(null);
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset"; // Or 'auto'
    }
    return () => {
      document.body.style.overflow = "unset"; // Cleanup on unmount
    };
  }, [isModalOpen]);

  useGSAP(
    () => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.inOut",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top 80%",
            end: "bottom 60%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Cards stagger animation
      gsap.fromTo(
        cardsRef.current,
        {
          opacity: 0,
          y: 100,
          scale: 0.9,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          ease: "power3.out",
          stagger: {
            amount: 1.2,
            from: "start",
          },
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
            end: "bottom 30%",
            toggleActions: "play none none reverse",
          },
        }
      );
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      className="bg-gray-50 py-16 px-4 md:px-10 sm:px-6 lg:px-8 md:py-[100px]"
    >
      {/* Modal */}
      <SpeakerBioModal
        speaker={selectedSpeaker}
        isOpen={isModalOpen}
        onClose={closeModal}
      />

      <div className="container mx-auto overflow-hidden">
        {/* Section Title */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="h-[1px] bg-gray-400 flex-1 max-w-[150px] md:max-w-[250px]"></div>
            <h2
              ref={titleRef}
              className="text-2xl md:text-3xl  lg:text-[40px] font-bold text-gray-900 mx-6 md:mx-8 font-sora tracking-wide"
            >
              FEATURED SPEAKERS
            </h2>
            <div className="h-[1px] bg-gray-400 flex-1 max-w-[150px] md:max-w-[250px]"></div>
          </div>
        </div>

        <ResponsiveSpeakerCarousel openModal={openModal} isModalOpen={isModalOpen} />
      </div>
    </section>
  );
};

export default FeaturedSpeakers;
